import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Icon } from "@sagot/ui";
import { useWorkspaceContext } from "../../../../session/workspace-context";
import type { WebhookOut } from "../../shared";

const P = "settings.webhooks.delivery.paused";

/** The Developer webhooks default workflow, as the endpoints report it: `null` before a workspace has it. */
export type EventDelivery = WebhookOut["event_delivery"];

/** Whether the workflow that sends events is paused (a paused one sends no event to any endpoint). */
export const deliveryPaused = (delivery: EventDelivery | undefined): delivery is NonNullable<EventDelivery> => delivery?.state === "paused";

/** Whether an endpoint in this mode is fed by that workflow (a workflows-only endpoint is called by workflow steps instead). */
export const receivesEvents = (mode: string): boolean => mode !== "workflows";

/** A warning above the endpoints (and inside the endpoint form) when the Developer webhooks workflow is paused, with the way to resume it. */
export function EventDeliveryNotice({ delivery, compact = false }: { delivery: NonNullable<EventDelivery>; compact?: boolean }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  return (
    <div className="rounded-md border border-warning bg-warning-soft p-3" role="status" data-testid="event-delivery-paused">
      <p className="flex items-center gap-2 text-sm font-bold">
        <Icon name="pause" className="size-4 shrink-0 text-warning" />
        {t(`${P}.title`)}
      </p>
      {compact ? null : <p className="mt-1 text-xs text-fg-muted">{t(`${P}.body`)}</p>}
      <AppLink className="mt-1.5 inline-block text-xs font-semibold text-primary-ink underline" href={consoleRoutes.workflow(summary.slug, delivery.workflow_id)}>{t(`${P}.action`)}</AppLink>
    </div>
  );
}

/** On one endpoint that would be sent events: they are paused. */
export function EventsPausedBadge() {
  const { t } = useTranslation();
  return (
    <Badge tone="warning" data-testid="events-paused-badge">
      <Icon name="pause" className="size-3" />
      {t(`${P}.badge`)}
    </Badge>
  );
}
